import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription, Observable, EMPTY } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { isEmptyString } from "../../helpers/utils";
import { DocumentLoaderService } from "../../services/document_loader.service";
import { ApplicationUtilsService } from "../../global_services/application_utils/application_utils.service";
import { EcertInputComponent } from "../../building-blocks/ecertnl-input/ecertnl-input.component";

@Component({
  selector: "app-check-certificate",
  templateUrl: "./check-certificate.component.html",
  styleUrls: ["./check-certificate.component.css"],
  standalone: false,
})
export class CheckCertificateComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  constructor(
    private httpClient: HttpClient,
    private documentUtils: DocumentLoaderService,
    private appService: ApplicationUtilsService,
    private route: ActivatedRoute
  ) {
    // Verificatienummer mag alleen cijfers bevatten, of kan helemaal leeg zijn.
    this.verificatiePattern = /^[0-9]*$/;
  }

  // Excluded array contains the characters which are excluded from the captcha-string.
  excludedArray = ["0", "1", "o", "l", "i", "O", "L", "I"];

  private subscriptions: Subscription[] = [];
  body = {
    captchaInput: "",
    certificateNumber: "",
    verificationNumber: "",
    withQrcode: "",
  };
  resultaten: any[] = [];
  resultatenTot: any;
  options: any;
  isResultatenOphalenBezig = false;
  errorTekst: any;
  certificaat: any;
  verificatiePattern: any;
  certificaatError = false;
  verificatieError = false;
  captchaError = false;
  withQrcode: any = "J";
  canvas: any;
  captchaText: any;
  captchaTextGenerator: any;
  private covErrorMessages = {
    foutmelding: "",
    headerFoutmelding: "",
    foutmeldingOverig: "",
  };

  @ViewChildren(EcertInputComponent)
  inputs: QueryList<EcertInputComponent>;

  ngOnInit() {
    this.getInitReferences();
    this.getOptions();
    this.body.certificateNumber = this.appService.getCertificateNumber();
    this.body.verificationNumber = this.appService.getVerificationNumber();
    this.body.captchaInput = this.appService.getCaptchaInput();
    this.covErrorMessages = this.appService.getCovErrorMessages();
    this.checkParameters();
  }

  ngAfterViewInit() {
    this.getCaptchaText(false);
  }

  getContextRoot() {
    return "cov";
  }

  getOptions() {
    const headers = new HttpHeaders({
      "Content-Type": "application/json; charset=utf-8",
    });
    this.options = { headers: headers };
  }

  hasErrors() {
    let inputsError = false;
    if (this.inputs != null) {
      this.inputs.forEach((input) => {
        inputsError = inputsError || input.hasErrors();
      });
    }
    return inputsError;
  }

  formatVerificationNumber(verificationNumber: String) {
    let formatValue = verificationNumber.replace(/-/g, "");
    if (formatValue.length >= 13) {
      formatValue = formatValue.substr(0, 12) + "-" + formatValue.substr(12);
    }
    if (formatValue.length >= 9) {
      formatValue = formatValue.substr(0, 8) + "-" + formatValue.substr(8);
    }
    if (formatValue.length >= 5) {
      formatValue = formatValue.substr(0, 4) + "-" + formatValue.substr(4);
    }
    return formatValue;
  }

  checkParameters() {
    this.route.queryParams.subscribe((params) => {
      if (params.hasOwnProperty("cc") && params.hasOwnProperty("vc")) {
        this.body.certificateNumber = params.cc;
        this.body.verificationNumber = this.formatVerificationNumber(params.vc);
        this.withQrcode = "J";
        this.checkCertificate(this.withQrcode);
      } else {
        this.withQrcode = "N";
      }
    });
  }

  verifyInputValues(withQrcode) {
    if (withQrcode === "J") {
      // Bij gebruik maken van een QR-code wordt het certificaat gecontroleerd en is er geen captcha-error.
      this.setCaptchaError(false);
      this.checkCertificate(withQrcode);
    } else if (this.body.captchaInput === this.captchaText) {
      // Bij handmatige invoer met een correcte captcha wordt het certificaat gecontroleerd
      // en wordt een reload gedaan van de captcha zonder captcha-foutmelding.
      this.checkCertificate(withQrcode);
      this.reloadCaptchaText(false);
    } else {
      // Bij handmatige invoer met een incorrecte captcha wordt het certificaat niet gecontroleerd
      // en wordt een reload gedaan van de captcha met een captcha-foutmelding.
      this.reloadCaptchaText(true);
    }
  }

  setCaptchaError(error: boolean) {
    if (this.inputs != null) {
      this.inputs.forEach((input) => {
        if (input.id === "captcha_input") {
          input.error = error;
        }
      });
    }
  }

  checkCertificate(withQrcode) {
    this.isResultatenOphalenBezig = true;
    this.certificaatError = false;
    this.verificatieError = false;

    if (this.hasErrors()) {
      return;
    }
    this.setResultaten(null);
    this.setResultatenOpgehaald(false);

    // Kopieer body naar certificaat en verwijder koppeltekens bij het controleren.
    this.body.withQrcode = withQrcode;
    this.certificaat = JSON.parse(JSON.stringify(this.body));
    this.certificaat.verificationNumber =
      this.certificaat.verificationNumber.replace(/-/g, "");
    this.certificaat.language = this.language;

    // Opslaan van input-values in de service
    this.appService.setCertificateNumber(this.certificaat.certificateNumber);
    this.appService.setVerificationNumber(this.certificaat.verificationNumber);
    this.appService.setCaptchaInput(this.body.captchaInput);

    let resError = null;
    this.setResultaatError(null);
    this.errorTekst = null;
    this.subscriptions.push(
      this.httpClient
        .post(
          "/" + this.getContextRoot() + "/checkCertificate",
          JSON.stringify(this.certificaat),
          this.options
        )
        .pipe(
          catchError((err: HttpErrorResponse, caught: Observable<any>) => {
            if (err.status === 0) {
              let updatedError = new HttpErrorResponse({
                ...err,
                statusText: this.covErrorMessages.foutmeldingOverig,
              });
              this.errorTekst =
                this.covErrorMessages.foutmelding +
                " " +
                updatedError.status +
                " (" +
                updatedError.statusText +
                ")";
            } else {
              this.errorTekst =
                this.covErrorMessages.foutmelding +
                " " +
                err.status +
                " (" +
                err.statusText +
                ")";
            }
            resError = {
              header: this.covErrorMessages.headerFoutmelding,
              tekst: this.errorTekst,
            };
            this.setResultaatError(resError);
            this.reloadCaptchaText(false);
            this.isResultatenOphalenBezig = false;
            return EMPTY;
          }),
          map((res: any) => {
            this.resultatenTot = res;
            if (this.resultatenTot != null) {
              if (this.resultatenTot.status === "OK") {
                this.setResultaten(this.resultatenTot.certList);
                this.setResultatenOpgehaald(true);
                this.isResultatenOphalenBezig = false;
              } else {
                let header;
                if (this.language === "ES") {
                  header = "Certificado no verificado";
                } else {
                  header = "Certificate not verified";
                }
                resError = {
                  header: header,
                  tekst: this.getErrorTekst(this.resultatenTot.foutmelding),
                };
                this.setResultaatError(resError);
                this.reloadCaptchaText(false);
                this.isResultatenOphalenBezig = false;
              }
            } else {
              this.isResultatenOphalenBezig = false;
            }
          })
        )
        .subscribe()
    );
  }

  getErrorTekst(foutmelding) {
    return foutmelding;
  }

  download() {
    const resultaten = this.getResultaten();
    const file = resultaten[0].document;
    const bestandsnaam = "certificate";
    const contentType = "application/pdf";
    this.documentUtils.openFileFromB64(file, bestandsnaam, contentType);
    this.setResultatenOpgehaald(false);
  }

  isDisabled() {
    return (
      this.hasErrors() ||
      this.body == null ||
      isEmptyString(this.body.certificateNumber) ||
      isEmptyString(this.body.verificationNumber) ||
      isEmptyString(this.body.captchaInput) ||
      this.body.verificationNumber.length !== 19
    );
  }

  getPrompt(prompt) {
    return this.appService.getPrompt(prompt);
  }

  get language() {
    return this.appService.getLanguage();
  }

  set language(language) {
    this.appService.setLanguage(language);
  }

  changeLanguage(language) {
    this.language = language;
    this.appService.setCertificateNumber(this.body.certificateNumber);
    this.appService.setVerificationNumber(this.body.verificationNumber);
    this.appService.setCaptchaInput(this.body.captchaInput);
    this.appService.setResultaatError(null);
  }

  getResultaten() {
    return this.appService.getResultaten();
  }
  setResultaten(resultaten) {
    this.appService.setResultaten(resultaten);
  }

  isResultatenOpgehaald() {
    return this.appService.isResultatenOpgehaald();
  }

  setResultatenOpgehaald(resultatenOpgehaald: boolean) {
    this.appService.setResultatenOpgehaald(resultatenOpgehaald);
  }

  getResultaatError() {
    return this.appService.getResultaatError();
  }

  setResultaatError(resultaatError: any) {
    this.appService.setResultaatError(resultaatError);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.resultatenTot = null;
  }

  getInitReferences() {
    this.canvas = document.getElementById("canvas");
    this.captchaText = "";
    this.captchaTextGenerator = () => {
      let generatedText = "";

      // capitalArray contains capital characters (ascii code 65-90), excluding characters from excludeArray
      const capitalArray = this.getArray(65, 90);

      // smallArray contains small characters (ascii code 97-122), excluding characters from excludeArray
      const smallArray = this.getArray(97, 122);

      // numberArray contains the characters from 0-9 (ascii code 48-57), excluding characters from excludeArray
      const numberArray = this.getArray(48, 57);

      // Total 9 characters: 3 capital, 3 small and 3 numbers
      for (let i = 0; i < 3; i++) {
        let randomNumber;
        randomNumber = this.getRandomNumber(1, capitalArray.length);
        generatedText += capitalArray[randomNumber - 1];
        randomNumber = this.getRandomNumber(1, smallArray.length);
        generatedText += smallArray[randomNumber - 1];
        randomNumber = this.getRandomNumber(1, numberArray.length);
        generatedText += numberArray[randomNumber - 1];
      }
      return generatedText;
    };
  }

  getArray(min: number, max: number) {
    // returns an array based on ascii numbers, excluding characters from excludeArray
    let char;
    const arr = [];
    for (let i = min; i <= max; i++) {
      char = String.fromCharCode(i);
      if (!(this.excludedArray.indexOf(char) > -1)) {
        arr.push(char);
      }
    }
    return arr;
  }

  getRandomNumber(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  drawStringOnCanvas(captchaText) {
    const ctx = this.canvas.getContext("2d");
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    const textColors = ["rgb(0,0,0)", "rgb(130,130,130)"];
    const letterSpace = 180 / captchaText.length;

    for (let i = 0; i < captchaText.length; i++) {
      const xInitialSpace = 25;
      ctx.font = "22px Roboto Mono";
      ctx.fillStyle = textColors[this.getRandomNumber(0, 1)];
      ctx.fillText(
        captchaText[i],
        xInitialSpace + i * letterSpace,
        this.getRandomNumber(25, 40),
        100
      );
    }
  }

  reloadCaptchaText(error: boolean) {
    this.body.captchaInput = "";
    this.appService.setCaptchaInput("");
    this.getCaptchaText(error);
  }

  getCaptchaText(error: boolean) {
    this.setCaptchaError(error);
    this.captchaText = this.captchaTextGenerator();
    // Randomize the characters in the captcha text
    this.captchaText = [].slice
      .call(this.captchaText)
      .sort(() => Math.random() - 0.5)
      .join("");
    this.drawStringOnCanvas(this.captchaText);
  }
}
